export interface HPORelatedTerm {
  id: string
  name: string
  hpoCode: string
  relationship: 'parent' | 'child' | 'sibling'
}

const HPO_RELATIONSHIPS: Record<string, { parent?: string; children?: string[]; siblings?: string[] }> = {
  'HP:0001250': {
    parent: 'HP:0012638',
    siblings: ['HP:0001336', 'HP:0002123', 'HP:0002353', 'HP:0002069', 'HP:0001249'],
    children: ['HP:0002123', 'HP:0007359', 'HP:0002121', 'HP:0011097', 'HP:0002133'],
  },
  'HP:0001336': {
    parent: 'HP:0012638',
    siblings: ['HP:0001250', 'HP:0002123', 'HP:0002353'],
  },
  'HP:0001276': {
    parent: 'HP:0003808',
    siblings: ['HP:0001252', 'HP:0001290', 'HP:0001257'],
    children: ['HP:0002063', 'HP:0006895', 'HP:0001300'],
  },
  'HP:0001252': {
    parent: 'HP:0003808',
    siblings: ['HP:0001276', 'HP:0001290', 'HP:0001257'],
    children: ['HP:0008947', 'HP:0008936', 'HP:0009062'],
  },
  'HP:0001347': {
    parent: 'HP:0001348',
    siblings: ['HP:0001315', 'HP:0001284', 'HP:0001270'],
  },
  'HP:0001315': {
    parent: 'HP:0001348',
    siblings: ['HP:0001347', 'HP:0001284', 'HP:0001270'],
  },
  'HP:0001254': {
    parent: 'HP:0033127',
    siblings: ['HP:0001259', 'HP:0001262', 'HP:0002329'],
  },
  'HP:0011968': {
    parent: 'HP:0012758',
    siblings: ['HP:0002194', 'HP:0001270', 'HP:0001249'],
    children: ['HP:0002015', 'HP:0002020', 'HP:0100022'],
  },
  'HP:0000252': {
    parent: 'HP:0000240',
    siblings: ['HP:0000256', 'HP:0004482', 'HP:0005484'],
  },
  'HP:0000256': {
    parent: 'HP:0000240',
    siblings: ['HP:0000252', 'HP:0004482', 'HP:0005484'],
  },
  'HP:0002179': {
    parent: 'HP:0001276',
    siblings: ['HP:0002063', 'HP:0006895', 'HP:0001300'],
  },
  'HP:0003537': {
    parent: 'HP:0010935',
    siblings: ['HP:0002149', 'HP:0003537'],
  },
}

const HPO_TERM_NAMES: Record<string, string> = {
  'HP:0001250': 'Seizure',
  'HP:0001336': 'Myoclonus',
  'HP:0002123': 'Generalized myoclonic seizure',
  'HP:0002353': 'EEG abnormality',
  'HP:0002069': 'Bilateral tonic-clonic seizure',
  'HP:0001249': 'Intellectual disability',
  'HP:0007359': 'Focal-onset seizure',
  'HP:0002121': 'Generalized non-motor seizure',
  'HP:0011097': 'Epileptic spasm',
  'HP:0002133': 'Status epilepticus',
  'HP:0012638': 'Abnormal nervous system physiology',
  'HP:0001276': 'Hypertonia',
  'HP:0001252': 'Hypotonia',
  'HP:0001290': 'Generalized hypotonia',
  'HP:0001257': 'Spasticity',
  'HP:0002063': 'Rigidity',
  'HP:0006895': 'Lower limb hypertonia',
  'HP:0001300': 'Parkinsonism',
  'HP:0003808': 'Abnormal muscle tone',
  'HP:0001347': 'Hyperreflexia',
  'HP:0001315': 'Hyporeflexia',
  'HP:0001284': 'Areflexia',
  'HP:0001270': 'Motor delay',
  'HP:0001348': 'Abnormal reflex',
  'HP:0001254': 'Lethargy',
  'HP:0001259': 'Coma',
  'HP:0001262': 'Excessive daytime somnolence',
  'HP:0002329': 'Drowsiness',
  'HP:0033127': 'Abnormality of consciousness',
  'HP:0011968': 'Feeding difficulties',
  'HP:0002194': 'Delayed gross motor development',
  'HP:0002015': 'Dysphagia',
  'HP:0002020': 'Gastroesophageal reflux',
  'HP:0100022': 'Abnormality of movement',
  'HP:0012758': 'Neurodevelopmental delay',
  'HP:0000252': 'Microcephaly',
  'HP:0000256': 'Macrocephaly',
  'HP:0004482': 'Relative macrocephaly',
  'HP:0005484': 'Postnatal microcephaly',
  'HP:0000240': 'Abnormality of skull size',
  'HP:0002179': 'Opisthotonos',
  'HP:0003537': 'Decreased plasma uric acid concentration',
  'HP:0002149': 'Hyperuricemia',
  'HP:0010935': 'Abnormality of purine metabolism',
  'HP:0008947': 'Infantile axial hypotonia',
  'HP:0008936': 'Axial hypotonia',
  'HP:0009062': 'Infantile onset hypotonia',
}

export function getRelatedTerms(termId: string): HPORelatedTerm[] {
  const relationships = HPO_RELATIONSHIPS[termId]
  if (!relationships) {
    return []
  }

  const related: HPORelatedTerm[] = []

  if (relationships.parent && HPO_TERM_NAMES[relationships.parent]) {
    related.push({
      id: relationships.parent,
      name: HPO_TERM_NAMES[relationships.parent],
      hpoCode: relationships.parent,
      relationship: 'parent',
    })
  }

  if (relationships.siblings) {
    for (const siblingId of relationships.siblings) {
      if (HPO_TERM_NAMES[siblingId] && siblingId !== termId) {
        related.push({
          id: siblingId,
          name: HPO_TERM_NAMES[siblingId],
          hpoCode: siblingId,
          relationship: 'sibling',
        })
      }
    }
  }

  if (relationships.children) {
    for (const childId of relationships.children) {
      if (HPO_TERM_NAMES[childId]) {
        related.push({
          id: childId,
          name: HPO_TERM_NAMES[childId],
          hpoCode: childId,
          relationship: 'child',
        })
      }
    }
  }

  return related
}

export function hasRelatedTerms(termId: string): boolean {
  return !!HPO_RELATIONSHIPS[termId]
}

export function getTermName(termId: string): string | undefined {
  return HPO_TERM_NAMES[termId]
}
